// src/pages/Cart.jsx
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2, Minus, Plus, ArrowLeft } from "lucide-react";
import { useCart } from "../context/CartContext";
import Currency from "../components/Currency";

export default function Cart() {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, cartTotal } = useCart();

  if (cart.length === 0) {
    return (
      <div className="animate-fadeIn text-center py-20">
        <ShoppingCart className="w-12 h-12 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-gray-400 mb-4">Your cart is empty.</p>
        <Link
          to="/dashboard/gifts"
          className="inline-flex items-center gap-1.5 text-sm text-purple-600 dark:text-purple-400 font-medium hover:underline"
        >
          <ArrowLeft className="w-4 h-4" /> Browse Gifts
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn max-w-2xl">
      <Link
        to="/dashboard/gifts"
        className="inline-flex items-center gap-1.5 text-sm text-purple-600 dark:text-purple-400 font-medium mb-4 hover:underline"
      >
        <ArrowLeft className="w-4 h-4" /> Continue Shopping
      </Link>

      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Your Cart</h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {cart.length} gift{cart.length > 1 ? "s" : ""} in your cart.
      </p>

      <div className="space-y-3 mb-6">
        {cart.map(({ gift, quantity }) => (
          <div
            key={gift._id}
            className="flex items-center gap-3.5 bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl p-3.5 sm:p-4"
          >
            <img src={gift.imageUrl} alt={gift.title} className="w-16 h-16 rounded-lg object-cover shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase truncate">
                {gift.title}
              </p>
              <p className="text-sm font-bold text-purple-600 dark:text-purple-400 mt-0.5">
                <Currency amount={gift.priceNgn} />
              </p>

              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => updateQuantity(gift._id, quantity - 1)}
                  disabled={quantity <= 1}
                  className="w-6 h-6 flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 w-5 text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => updateQuantity(gift._id, quantity + 1)}
                  className="w-6 h-6 flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                <Currency amount={gift.priceNgn * quantity} />
              </p>
              <button onClick={() => removeFromCart(gift._id)} className="text-red-500 hover:text-red-600 transition">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
          <p className="text-xl font-bold text-gray-900 dark:text-gray-100"><Currency amount={cartTotal} /></p>
        </div>
        <button
          onClick={() => navigate("/dashboard/gifts/checkout", { state: { cartMode: true } })}
          className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition"
        >
          Proceed to Checkout
        </button>
      </div>
    </div>
  );
}